import { useFormikContext } from 'formik';
import React, { FormEvent, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cartProductSelector } from '~redux/cart/selectors';
import { productItemSelector } from '~redux/product/selectors';
import {
  useSelectAvailableInventoryBySku,
  useSelectInventoryBySku,
  useSelectIsOutOfStockBySku,
} from '~view/hooks/inventory';
import FieldWithValidity from '~view/widgets/FieldWithValidity';

type InputQuantityProps = {
  name: string;
  lot: number;
  minimum: number;
  brandId: string;
  productId: string;
  productTypeId?: string;
  sku?: string;
  cart?: boolean;
};

const InputQuantity: React.FC<InputQuantityProps> = ({
  name,
  lot,
  minimum,
  brandId,
  productId,
  productTypeId,
  sku,
  cart,
}) => {
  const {
    values: { quantities },
    setFieldValue,
  } = useFormikContext<{ quantities: Record<string, number> }>();

  const product = useSelector(productItemSelector);
  const cartProduct = useSelector(cartProductSelector);
  const isOutOfStock = useSelectIsOutOfStockBySku(
    brandId,
    sku,
    productId,
    productTypeId
  );
  const inventory = useSelectInventoryBySku(
    brandId,
    sku,
    productId,
    productTypeId
  );
  const availableInventory = useSelectAvailableInventoryBySku(
    brandId,
    sku,
    productId,
    productTypeId
  );
  const preOrder = useMemo(
    () => (product ?? cartProduct)?.product_preorder === 1,
    [product]
  );
  const limit = useMemo(() => {
    // 予約販売の場合は在庫数を無視する
    if (preOrder) {
      return 1000;
    }

    return Math.max((cart ? inventory : availableInventory) ?? 1000, 0);
  }, [cart, inventory, availableInventory]);
  const max = useMemo(() => limit - (limit % lot), [limit, lot]);

  const onBlur = useCallback(
    (value: string) => {
      const qty = Number(value);
      if (!qty || isNaN(qty)) {
        setFieldValue(`quantities.${name}`, 0);
        return;
      }
      if (qty < minimum) {
        setFieldValue(`quantities.${name}`, minimum);
        return;
      }
      const over = lot - (qty % lot);
      setFieldValue(
        `quantities.${name}`,
        Math.min(qty + (over === lot ? 0 : over), max)
      );
    },
    [name, lot, minimum, max]
  );

  if (!preOrder && isOutOfStock) {
    return (
      <div
        className="text-muted"
        style={{ width: '130px', textAlign: 'center', padding: '5px' }}
      >
        売り切れ
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center justify-content-between w-100">
      <button
        type="button"
        tabIndex={-1}
        onClick={() =>
          setFieldValue(
            `quantities.${name}`,
            Math.max((quantities[name] ?? 0) - lot, 0)
          )
        }
        className="bg-white"
        style={{
          border: 0,
          width: '30px',
          padding: '5px 0',
        }}
      >
        -
      </button>
      <FieldWithValidity
        name={`quantities.${name}`}
        type="text"
        onBlur={(e: FormEvent<HTMLInputElement>) =>
          onBlur(e.currentTarget.value)
        }
        className="bg-white"
        style={{
          width: '60px',
          height: 'auto',
          padding: '5px',
          textAlign: 'center',
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => {
          setFieldValue(
            `quantities.${name}`,
            Math.min((quantities[name] ?? 0) + lot, max)
          );
        }}
        className="bg-white"
        style={{
          border: 0,
          width: '30px',
          padding: '5px 0',
        }}
      >
        +
      </button>
    </div>
  );
};

export default InputQuantity;
